import React, { useEffect, useState } from 'react';
import {
    DocumentViewer,
    ProviderMail,
    ProviderMailCart,
    ProviderMailCartShipment,
    SupportMail,
    Spinner,
} from '../../../..';
import { formatDate } from '../../../../services/primitives/date-utils';
import _ from 'lodash';
import ContentLoader from 'react-content-loader';
import { renderAddressGroup as renderAddressGroupFromDataTable } from '../../../data-table';
import { IMailDetailProps } from './mail-detail.types';
import * as S from '../claim-timeline.styles';

const LinePlaceholder = React.memo(() => (
    <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
        <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
    </ContentLoader>
));

export const MailDetail: React.FunctionComponent<IMailDetailProps> = (props): React.ReactElement => {
    const { mail, appType, downloadDocument, loadMailCart, hideShipmentInformation = false } = props;

    const [loading, setLoading] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<{ url?: string } | undefined>();
    const [mailCart, setMailCart] = useState<ProviderMailCart | undefined>();

    const documentId = mail.documentId;

    useEffect(() => {
        (async (): Promise<void> => {
            if (mail && documentId) {
                setLoading(true);
                try {
                    const url = await downloadDocument(documentId);
                    setDocumentUrl(url);
                } finally {
                    setLoading(false);
                }
            }
        })();
    }, [mail, documentId, downloadDocument]);

    useEffect(() => {
        if (appType !== 'provider' || hideShipmentInformation || !mail.mailCartId || !loadMailCart) {
            return;
        }
        (async (): Promise<void> => {
            const cart = await loadMailCart(mail.mailCartId!);
            setMailCart(cart as ProviderMailCart | undefined);
        })();
    }, [appType, hideShipmentInformation, mail.mailCartId, loadMailCart]);

    const renderDocument = () => {
        if (documentUrl?.url) {
            return <DocumentViewer url={documentUrl.url} />;
        }
    };

    const renderMailCartItem = (item: ProviderMail): React.ReactElement => (
        <div key={item.id}>
            {item.receivedDate ? formatDate(item.receivedDate, 'MMM dd, yyyy hh:mm') : 'Not received'} - {item.externalId}
        </div>
    );

    const renderShipment = (shipment?: ProviderMailCartShipment): React.ReactElement | undefined => {
        if (!shipment) return undefined;
        return (
            <>
                <S.ExpandTR>
                    <S.ExpandTH>From</S.ExpandTH>
                    <S.ExpandTD>{shipment.from}</S.ExpandTD>
                </S.ExpandTR>
                {shipment.to && (
                    <S.ExpandTR>
                        <S.ExpandTH>To</S.ExpandTH>
                        <S.ExpandTD>{renderAddressGroupFromDataTable([shipment.to])}</S.ExpandTD>
                    </S.ExpandTR>
                )}
                {shipment.method && (
                    <S.ExpandTR>
                        <S.ExpandTH>Method</S.ExpandTH>
                        <S.ExpandTD>
                            {_.join(_.compact([shipment.method.name, shipment.method.estimate]), ', ')}
                        </S.ExpandTD>
                    </S.ExpandTR>
                )}
                <S.ExpandTR>
                    <S.ExpandTH>Instructions</S.ExpandTH>
                    <S.ExpandTD>{shipment.instructions}</S.ExpandTD>
                </S.ExpandTR>
                <S.ExpandTR>
                    <S.ExpandTH>Tracking</S.ExpandTH>
                    <S.ExpandTD>
                        {shipment.tracking?.courier}
                        {shipment.tracking?.number}
                        <br />
                        <S.StyledLink href={shipment.tracking?.url}>Track Shipment</S.StyledLink>
                    </S.ExpandTD>
                </S.ExpandTR>
                <S.ExpandTR>
                    <S.ExpandTH>Weight</S.ExpandTH>
                    <S.ExpandTD>{shipment.weight} oz.</S.ExpandTD>
                </S.ExpandTR>
            </>
        );
    };

    const renderMailCart = () => {
        if (!mailCart) return null;
        const items = _.map(mailCart?.items, item => renderMailCartItem(item as ProviderMail));
        return (
            <>
                <S.ExpandSegment />
                <S.ExpandTable expand={true}>
                    <S.ExpandTBody>
                        <S.ExpandTR>
                            <S.ExpandTitleTH colSpan={2}> Mail Cart </S.ExpandTitleTH>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Name</S.ExpandTH>
                            <S.ExpandTD>{mailCart.name}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Items</S.ExpandTH>
                            <S.ExpandTD>{items}</S.ExpandTD>
                        </S.ExpandTR>
                        {renderShipment(mailCart.shipment as ProviderMailCartShipment | undefined)}
                    </S.ExpandTBody>
                </S.ExpandTable>
            </>
        );
    };

    if (appType === 'support') {
        return (
            <S.ExpandWrap key={mail.id}>
                <S.ExpandContainer>
                    <S.ExpandTable>
                        <S.ExpandTBody>
                            <S.ExpandTR>
                                <S.ExpandTH>ID</S.ExpandTH>
                                <S.ExpandTD>{(mail as SupportMail).id}</S.ExpandTD>
                            </S.ExpandTR>
                        </S.ExpandTBody>
                    </S.ExpandTable>
                    {loading && <LinePlaceholder />}
                    {renderDocument()}
                </S.ExpandContainer>
            </S.ExpandWrap>
        );
    }

    return (
        <S.ExpandWrap key={mail.id}>
            {loading ? (
                <Spinner />
            ) : (
                <S.ExpandContainer>
                    {renderDocument()}
                    {!hideShipmentInformation && mailCart && renderMailCart()}
                </S.ExpandContainer>
            )}
        </S.ExpandWrap>
    );
};
